import React, { useMemo } from 'react';
import {
  Box,
  Button,
  Container,
  Flex,
  Heading,
  Icon,
  Image,
  SimpleGrid,
  Text,
  VStack,
  Badge,
  Link as ChakraLink,
  useColorModeValue,
} from '@chakra-ui/react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  MdQrCode2,
  MdPhotoSizeSelectLarge,
  MdApps,
  MdLock,
  MdCloudOff,
  MdSpeed,
  MdWifi,
  MdCheckCircle,
  MdArrowForward,
  MdConstruction,
  MdCode,
  MdSelfImprovement,
  MdGrid3X3,
  MdCalculate,
  MdAutoFixHigh,
  MdDraw,
  MdPassword,
  MdPlayCircle,
  MdPictureAsPdf,
  MdArchitecture,
} from 'react-icons/md';
import bmcIcon from '../assets/images/bmc-icon.png';
import { BMC_LINK } from './config';
import { sendEvent } from './analytics';
import { useStreamPlayerUnlocked } from './streamPlayerUnlock';

const MotionBox = motion(Box);

// ─── Data ────────────────────────────────────────────────────────────────────

const TOOL_SECTIONS = [
  {
    title: 'Productivity',
    tools: [
      {
        path: '/focuslounge',
        icon: MdSelfImprovement,
        color: 'purple',
        gradient: 'linear(135deg, #1e1040, #4c1d95, #1e1040)',
        label: 'Focus Lounge',
        description:
          'Premium Pomodoro focus timer with ambient themes, music integration (Spotify / YouTube), session stats, and an immersive workspace feel.',
        badge: 'New',
      },
      {
        path: '/texteditor',
        icon: MdCode,
        color: 'teal',
        gradient: 'linear(135deg, #0f2027, #203a43, #2c5364)',
        label: 'Text Editor',
        description:
          'Multi-tab code and text editor with syntax highlighting for 24 languages, text transformations, and local file open/save — all in the browser.',
        badge: null,
      },
      {
        path: '/text-cleaner',
        icon: MdAutoFixHigh,
        color: 'teal',
        gradient: 'linear(135deg, #0d9488, #115e59, #134e4a)',
        label: 'Text Cleaner',
        description:
          'Paste messy text from PDFs, email, or the web and get a clean version instantly — smart line merging, Unicode fixes, presets, and one-click copy. No server.',
        badge: null,
      },
    ],
  },
  {
    title: 'Generators',
    tools: [
      {
        path: '/qrcodegenerator',
        icon: MdQrCode2,
        color: 'blue',
        gradient: 'linear(135deg, #667eea, #764ba2)',
        label: 'QR Code',
        description:
          'Create QR codes for URLs, Wi-Fi, contacts, events, locations and more. Fully customisable design with logo support.',
        badge: 'Popular',
      },
      {
        path: '/appiconmaker',
        icon: MdApps,
        color: 'green',
        gradient: 'linear(135deg, #11998e, #38ef7d)',
        label: 'App Icon',
        description:
          'Generate every required icon size for iOS, Android, macOS, watchOS and Web from a single 1024×1024 source image.',
        badge: null,
      },
      {
        path: '/password-generator',
        icon: MdPassword,
        color: 'teal',
        gradient: 'linear(135deg, #1e293b, #0f766e, #64748b)',
        label: 'Password Generator',
        description:
          'Create strong random passwords or readable passphrases in one calm screen — copy in a tap, live strength feedback, and zero data leaving your browser.',
        badge: 'New',
      },
    ],
  },
  {
    title: 'Document Tools',
    tools: [
      {
        path: '/pdf-studio',
        icon: MdPictureAsPdf,
        color: 'red',
        gradient: 'linear(135deg, #1e1b4b, #312e81, #7f1d1d)',
        label: 'PDF Studio',
        description:
          'Organize PDF pages with drag-and-drop reordering and one-click export — all processed locally in your browser, never uploaded.',
        badge: 'New',
      },
    ],
  },
  {
    title: 'Media Tools',
    tools: [
      {
        path: '/imageresizer',
        icon: MdPhotoSizeSelectLarge,
        color: 'orange',
        gradient: 'linear(135deg, #f093fb, #f5576c)',
        label: 'Image Resizer',
        description:
          'Batch-resize App Store and Google Play Store screenshots to all required portrait and landscape dimensions.',
        badge: null,
      },
      {
        path: '/image-marker',
        icon: MdDraw,
        color: 'purple',
        gradient: 'linear(135deg, #312e81, #6366f1, #be185d)',
        label: 'Image Marker',
        description:
          'Paste or drop an image, draw rectangles, arrows, text, and pixel blur on a layered canvas, then export or copy at full resolution — all in your browser.',
        badge: 'New',
      },
      {
        path: '/stream-player',
        icon: MdPlayCircle,
        color: 'teal',
        gradient: 'linear(135deg, #0f172a, #0d9488, #1e3a5f)',
        label: 'Stream Player',
        description:
          'A calm browser IPTV workspace: add Xtream or M3U sources (stored only on your device), browse live TV and VOD, favorites, search, and in-app playback.',
        badge: 'New',
      },
    ],
  },
  {
    title: 'Utilities',
    tools: [
      {
        path: '/calculator',
        icon: MdCalculate,
        color: 'teal',
        gradient: 'linear(135deg, #134e4a, #0e7490, #1e3a5f)',
        label: 'Calculator',
        description:
          'Fast client-side arithmetic with percent, sign toggle, and keyboard shortcuts — rectangular keys and a clean panel that fits the toolkit, not a phone clone.',
        badge: null,
      },
      {
        path: '/flowspace',
        icon: MdArchitecture,
        color: 'cyan',
        gradient: 'linear(135deg, #0c4a6e, #0369a1, #1e3a8a)',
        label: 'FlowSpace',
        description:
          'Plan rooms, layouts, and flows visually. A simple drag-and-drop canvas for floor plans, furniture layouts, uploaded images, and flowcharts — saved only in your browser.',
        badge: 'New',
      },
    ],
  },
  {
    title: 'Fun',
    tools: [
      {
        path: '/tictactoe',
        icon: MdGrid3X3,
        color: 'teal',
        gradient: 'linear(135deg, #0f172a, #134e4a, #0d9488)',
        label: 'Tic Tac Toe',
        description:
          'Play solo or with a friend — crisp 3×3 gameplay, smart AI (easy to unbeatable), scores saved locally, and a board designed to feel instant.',
        badge: null,
      },
    ],
  },
];

const TRUST_POINTS = [
  { icon: MdCloudOff,   label: 'No uploads',      desc: 'Files never leave your device' },
  { icon: MdLock,       label: 'No account',       desc: 'Zero sign-up, zero data collection' },
  { icon: MdCheckCircle,label: 'No tracking',      desc: 'Only anonymous page-view analytics' },
  { icon: MdWifi,       label: 'Works offline',    desc: 'Everything runs in the browser' },
];

const FEATURES = [
  { icon: MdSpeed,   label: 'Instant',  desc: 'Results appear as you type — no waiting' },
  { icon: MdLock,    label: 'Private',  desc: '100% client-side, nothing sent to any server' },
  { icon: MdWifi,    label: 'Offline',  desc: 'Works without an internet connection once loaded' },
  { icon: MdApps,    label: 'Free',     desc: 'All tools are free, forever — no paywalls' },
];

const COMING_SOON = [
  { icon: '🎬', label: 'Video Compressor',   desc: 'Compress MP4 / MOV locally in the browser' },
  { icon: '🎨', label: 'Color Palette',      desc: 'Extract palettes from images and export tokens' },
  { icon: '📊', label: 'CSV Tools',          desc: 'Clean, convert, and transform CSV files client-side' },
];

// ─── Section helpers ──────────────────────────────────────────────────────────

const SectionHeading = ({ tag, title, subtitle }) => (
  <VStack spacing={3} mb={12} textAlign="center">
    {tag && (
      <Badge colorScheme="blue" variant="subtle" borderRadius="full" px={3} py={1} fontSize="xs" textTransform="uppercase" letterSpacing="wide">
        {tag}
      </Badge>
    )}
    <Heading as="h2" size="xl" fontWeight="800" letterSpacing="-0.5px">{title}</Heading>
    {subtitle && <Text fontSize="lg" color="gray.500" maxW="520px">{subtitle}</Text>}
  </VStack>
);

// ─── Main component ───────────────────────────────────────────────────────────

const LandingPage = () => {
  const streamPlayerUnlocked = useStreamPlayerUnlocked();
  const toolSections = useMemo(() => {
    if (streamPlayerUnlocked) return TOOL_SECTIONS;
    return TOOL_SECTIONS.map((sec) => ({
      ...sec,
      tools: sec.tools.filter((t) => t.path !== '/stream-player'),
    })).filter((sec) => sec.tools.length > 0);
  }, [streamPlayerUnlocked]);

  const cardBg      = useColorModeValue('white', 'gray.800');
  const cardBorder  = useColorModeValue('gray.100', 'gray.700');
  const sectionBg   = useColorModeValue('gray.50', 'gray.900');
  const darkStripBg = useColorModeValue('gray.900', 'gray.950');
  const mutedText   = useColorModeValue('gray.500', 'gray.400');

  return (
    <Box>

      {/* ── 1. HERO ─────────────────────────────────────────────────────── */}
      <Box
        bgGradient="linear(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%)"
        pt={{ base: 20, md: 26 }}
        pb={{ base: 16, md: 22 }}
        px={4}
        overflow="hidden"
        position="relative"
      >
        {/* Decorative blobs */}
        <Box position="absolute" top="-80px" right="-80px" w="400px" h="400px"
          borderRadius="full" bg="purple.500" opacity={0.12} filter="blur(80px)" />
        <Box position="absolute" bottom="-60px" left="-60px" w="300px" h="300px"
          borderRadius="full" bg="blue.400" opacity={0.12} filter="blur(70px)" />

        <Box textAlign="center" position="relative">
          <MotionBox
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Heading
              as="h1"
              fontSize={{ base: '3xl', md: '5xl', lg: '6xl' }}
              fontWeight="900"
              color="white"
              letterSpacing="-1px"
              lineHeight={1.1}
              maxW="800px"
              mx="auto"
            >
              Free browser tools that{' '}
              <Text as="span" bgGradient="linear(90deg, #667eea, #f093fb)" bgClip="text">
                respect your privacy
              </Text>
            </Heading>
          </MotionBox>
        </Box>
      </Box>

      {/* ── 2. TOOLS GRID (by category) ─────────────────────────────────── */}
      <Box bg={sectionBg} py={20} px={4}>
        <Container maxW="container.lg">
          <VStack align="stretch" spacing={14}>
            {toolSections.map((section) => (
              <Box key={section.title}>
                <Text
                  fontSize="sm"
                  fontWeight="800"
                  color="gray.700"
                  letterSpacing="-0.02em"
                  mb={5}
                >
                  {section.title}
                </Text>
                <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6}>
                  {section.tools.map((tool, i) => (
                    <MotionBox
                      key={tool.path}
                      initial={{ opacity: 0, y: 24 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4, delay: i * 0.06 }}
                      whileHover={{ y: -4 }}
                    >
                      <Box
                        bg={cardBg}
                        borderWidth="1px"
                        borderColor={cardBorder}
                        borderRadius="2xl"
                        overflow="hidden"
                        shadow="sm"
                        h="100%"
                        display="flex"
                        flexDirection="column"
                        transition="box-shadow 0.2s"
                        _hover={{ shadow: 'lg' }}
                      >
                        <Box position="relative" bgGradient={tool.gradient} p={5}>
                          {tool.badge && (
                            <Badge
                              position="absolute"
                              top="calc(1rem - 13px)"
                              right={4}
                              bg="whiteAlpha.300"
                              color="white"
                              borderRadius="full"
                              px={2.5}
                              py={0.5}
                              fontSize="xs"
                              textTransform="uppercase"
                              letterSpacing="0.04em"
                            >
                              {tool.badge}
                            </Badge>
                          )}
                          <Flex align="center" gap={3} pr={tool.badge ? { base: '5.5rem', sm: '5rem' } : 0}>
                            <Box bg="whiteAlpha.300" borderRadius="xl" p={2.5} flexShrink={0}>
                              <Icon as={tool.icon} color="white" boxSize={6} />
                            </Box>
                            <Text color="white" fontWeight="bold" fontSize="md" lineHeight="short">
                              {tool.label}
                            </Text>
                          </Flex>
                        </Box>
                        <Box p={5} flex={1} display="flex" flexDirection="column">
                          <Text fontSize="sm" color={mutedText} flex={1} mb={5}>
                            {tool.description}
                          </Text>
                          <Button
                            as={Link}
                            to={tool.path}
                            colorScheme={tool.color}
                            variant="outline"
                            size="sm"
                            borderRadius="lg"
                            rightIcon={<MdArrowForward />}
                            w="full"
                          >
                            Open Tool
                          </Button>
                        </Box>
                      </Box>
                    </MotionBox>
                  ))}
                </SimpleGrid>
              </Box>
            ))}
          </VStack>
        </Container>
      </Box>

      {/* ── 3. TRUST SECTION ─────────────────────────────────────────────── */}
      <Box bg={cardBg} py={20} px={4}>
        <Container maxW="container.lg">
          <SectionHeading
            tag="Privacy-first"
            title="Your files never leave your device"
            subtitle="Everything is processed inside your browser. We never see your data — not even for a millisecond."
          />
          <SimpleGrid columns={{ base: 1, sm: 2, md: 4 }} spacing={6}>
            {TRUST_POINTS.map((p) => (
              <VStack
                key={p.label}
                p={6}
                borderWidth="1px"
                borderColor={cardBorder}
                borderRadius="2xl"
                spacing={3}
                align="flex-start"
                bg={sectionBg}
              >
                <Box bg="green.50" borderRadius="lg" p={2}>
                  <Icon as={p.icon} color="green.500" boxSize={5} />
                </Box>
                <Text fontWeight="bold" fontSize="sm">{p.label}</Text>
                <Text fontSize="xs" color={mutedText}>{p.desc}</Text>
              </VStack>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      {/* ── 4. FEATURE STRIP ─────────────────────────────────────────────── */}
      <Box bg={darkStripBg} py={16} px={4}>
        <Container maxW="container.lg">
          <SimpleGrid columns={{ base: 1, sm: 2, md: 4 }} spacing={8}>
            {FEATURES.map((f) => (
              <Flex key={f.label} align="flex-start" gap={4}>
                <Box
                  bg="whiteAlpha.100"
                  borderRadius="xl"
                  p={2.5}
                  flexShrink={0}
                  mt={0.5}
                >
                  <Icon as={f.icon} color="purple.300" boxSize={5} />
                </Box>
                <Box>
                  <Text color="white" fontWeight="bold" fontSize="sm" mb={0.5}>{f.label}</Text>
                  <Text color="whiteAlpha.600" fontSize="xs">{f.desc}</Text>
                </Box>
              </Flex>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      {/* ── 5. COMING SOON ───────────────────────────────────────────────── */}
      <Box bg={sectionBg} py={20} px={4}>
        <Container maxW="container.lg">
          <SectionHeading
            tag="Coming soon"
            title="More tools on the way"
            subtitle="We're building more privacy-first browser tools. No sign-up required to be notified."
          />
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
            {COMING_SOON.map((t) => (
              <Box
                key={t.label}
                p={6}
                borderWidth="1px"
                borderColor={cardBorder}
                borderRadius="2xl"
                bg={cardBg}
                opacity={0.65}
                position="relative"
                overflow="hidden"
              >
                <Badge
                  position="absolute"
                  top={4}
                  right={4}
                  colorScheme="purple"
                  variant="subtle"
                  borderRadius="full"
                  fontSize="xs"
                  display="flex"
                  alignItems="center"
                  gap={1}
                >
                  <Icon as={MdConstruction} boxSize={3} />
                  Soon
                </Badge>
                <Text fontSize="3xl" mb={3}>{t.icon}</Text>
                <Text fontWeight="bold" fontSize="sm" mb={1}>{t.label}</Text>
                <Text fontSize="xs" color={mutedText}>{t.desc}</Text>
              </Box>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      {/* ── 6. DONATE ────────────────────────────────────────────────────── */}
      <Box bg={cardBg} py={16} px={4}>
        <Container maxW="container.sm">
          <VStack spacing={4} textAlign="center">
            <Text fontSize="2xl">☕</Text>
            <Heading as="h3" size="md" fontWeight="700">
              Enjoying the tools?
            </Heading>
            <Text fontSize="sm" color={mutedText} maxW="400px">
              VoraKit is free and will always be free. If it saves you time,
              a small donation helps keep the lights on.
            </Text>
            <Button
              as={ChakraLink}
              href={BMC_LINK}
              colorScheme="yellow"
              isExternal
              size="md"
              borderRadius="xl"
              px={8}
              onClick={() => sendEvent('Landing', 'Donate', 'clicked')}
            >
              <Image src={bmcIcon} boxSize="18px" mr={2} alt="" />
              Buy me a coffee
            </Button>
          </VStack>
        </Container>
      </Box>

    </Box>
  );
};

export default LandingPage;
